import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { ImageKind } from 'shared/utils/image/upscale/constants';
import { UpscaleFailureReason } from 'shared/utils/image/upscale/upscale-error';
import { defaultTargetSide, estimateWasmSeconds, targetSize } from 'shared/utils/image/upscale/upscale-plan';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { ProgressBar, ProgressTrack, Stage, StageOverlay } from './styles';
import { ImageSize, UpscaleModalProps, UpscaleState } from './types';
import { useImageUpscale } from './use-image-upscale';

const FAILURE_MESSAGES: Record<UpscaleFailureReason, MessageDescriptor> = {
  'model-load': msg`Не удалось загрузить модель. Проверьте соединение и попробуйте ещё раз.`,
  inference: msg`Улучшение прервалось. Попробуйте ещё раз.`,
  unsupported: msg`Браузер не умеет улучшать изображения. Откройте админку в свежем Chrome, Edge или Firefox.`,
  decode: msg`Не удалось прочитать картинку. Попробуйте другой файл.`,
  aborted: msg`Улучшение прервалось. Попробуйте ещё раз.`,
};

/** Повтор имеет смысл, только когда сбой не в самом файле и не в браузере. */
const RETRYABLE: UpscaleFailureReason[] = ['model-load', 'inference'];

const SUBTITLES: Record<ImageKind, MessageDescriptor> = {
  graphic: msg`ИИ увеличит логотип и сделает контуры чётче. Сравните и решите, что сохранить.`,
  photo: msg`ИИ увеличит фото. Сравните и решите, что сохранить.`,
};

function formatSize({ width, height }: ImageSize): string {
  return `${width}×${height} px`;
}

/**
 * Оценка без видеокарты — приблизительная и сверху, как советует NN/g для долгих операций:
 * человеку важно понять «секунды или минуты», а не точное число.
 */
function useWasmEstimate(kind: ImageKind, state: UpscaleState): string | null {
  const { t } = useLingui();
  if (state.status !== 'processing' || state.backend !== 'wasm') return null;

  const seconds = estimateWasmSeconds(kind, state.total - state.done);
  if (seconds < 60) return t`Без видеокарты это займёт меньше минуты`;

  const minutes = Math.ceil(seconds / 60);
  return t`Без видеокарты это займёт до ${minutes} мин`;
}

interface ResultStageProps {
  state: UpscaleState;
  kind: ImageKind;
  onRetry: () => void;
}

function ResultStage({ state, kind, onRetry }: ResultStageProps) {
  const { t } = useLingui();
  const estimate = useWasmEstimate(kind, state);

  if (state.status === 'done') {
    return <img src={state.url} alt={t`Стало`} />;
  }

  if (state.status === 'error') {
    return (
      <StageOverlay role="alert">
        <UDText variant="text-2">{t(FAILURE_MESSAGES[state.reason])}</UDText>
        {RETRYABLE.includes(state.reason) && (
          <UDButton variant="secondary" slim onClick={onRetry}>
            <span>
              <Trans>Повторить</Trans>
            </span>
          </UDButton>
        )}
      </StageOverlay>
    );
  }

  if (state.status === 'loading-model') {
    return (
      <StageOverlay aria-busy>
        <ClipLoader color="currentColor" size={28} />
        <UDText variant="text-2">
          <Trans>Загружаем модель…</Trans>
        </UDText>
      </StageOverlay>
    );
  }

  const percent = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;

  return (
    <StageOverlay aria-busy>
      <UDText variant="text-2">
        <Trans>Улучшаем… {percent}%</Trans>
      </UDText>
      <ProgressTrack role="progressbar" aria-label={t`Улучшение`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <ProgressBar style={{ width: `${percent}%` }} />
      </ProgressTrack>
      {estimate && <UDText variant="text-3">{estimate}</UDText>}
    </StageOverlay>
  );
}

/**
 * «Улучшить изображение»: ИИ-апскейл с предпросмотром «Было / Стало».
 *
 * Не автоматически, а по решению человека: модель может исказить мелкий текст логотипа и
 * черты лица (`.claude/docs/image-upscale.md`, «Model comparison»). Поэтому два исхода —
 * «Применить» и «Оставить как есть», а закрытие посреди обработки обрывает воркер.
 *
 * Модалка монтируется только на время показа: размонтирование — та же отмена.
 */
export function UpscaleModal({ file, kind, size, onClose, upscaler }: UpscaleModalProps) {
  const { t } = useLingui();
  const { state, retry, abort } = useImageUpscale({ file, kind, upscaler });
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSkip = useCallback(() => {
    abort();
    onClose(null);
  }, [abort, onClose]);

  const handleApply = useCallback(() => {
    if (state.status === 'done') onClose(state.file);
  }, [onClose, state]);

  const resultSize = targetSize(size, defaultTargetSide(kind));

  return (
    <Modal show onClose={handleSkip} size="lg">
      <Modal.Header title={t`Улучшить изображение`} subtitle={t(SUBTITLES[kind])} />
      <Modal.Body>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <figure className="m-0 flex flex-col gap-2">
            <UDText variant="text-2-bold">
              <Trans>Было</Trans>
            </UDText>
            <Stage>{sourceUrl && <img src={sourceUrl} alt={t`Было`} />}</Stage>
            <UDText variant="text-3" className="text-inkSecondary">
              {formatSize(size)}
            </UDText>
          </figure>
          <figure className="m-0 flex flex-col gap-2">
            <UDText variant="text-2-bold">
              <Trans>Стало</Trans>
            </UDText>
            <Stage>
              <ResultStage state={state} kind={kind} onRetry={retry} />
            </Stage>
            <UDText variant="text-3" className="text-inkSecondary">
              {formatSize(resultSize)}
            </UDText>
          </figure>
        </div>
        {kind === 'photo' && (
          <UDText variant="text-2" className="text-darkYellow-800 mt-4">
            <Trans>Проверьте лицо: ИИ мог изменить черты.</Trans>
          </UDText>
        )}
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="secondary" onClick={handleSkip}>
          <span>
            <Trans>Оставить как есть</Trans>
          </span>
        </UDButton>
        <UDButton variant="primary" disabled={state.status !== 'done'} onClick={handleApply}>
          <span>
            <Trans>Применить</Trans>
          </span>
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}

export default UpscaleModal;
